/**
 * Rounds — the view-model both trees read (docs/ROUNDS.md §4.4).
 *
 * This is the app's answer to the two mirrors' `vals()`:
 * `design/GT Round.dc.html` and `design/GT Mobile Round.dc.html`, plus the
 * shells' `rVals()` (chip, join card, START button). Copy is theirs verbatim;
 * every computed value is evaluated here from `lib/round.ts` and the store,
 * never eyeballed. Layout numbers that depend on the viewport are NOT here —
 * `roundLayout()` takes `w`/`h` explicitly so the store stays viewport-blind.
 *
 * `now` is SERVER time (`s.serverNow()`), because the one thing the wheel is
 * computed from is the spin record's server stamp. Callers that re-render on
 * a frame pass the same clock; nothing here reads `Date.now()`.
 */

import { ago } from '../lib/format.ts';
import { chipOf, fallGradient, initialsOf } from '../lib/format.ts';
import { artCands, passBoard } from '../lib/logic.ts';
import { tagLensOf } from './selectors.ts';
import type { ArtCand } from '../lib/types.ts';
import {
  effRank,
  effTierLabel,
  groupNoms,
  R_QUOTA,
  rGeom,
  roundShut,
  rTier,
  spinPose,
  ticketsFor,
  type RoundTier,
  type Slice,
} from '../lib/round.ts';
import type { RoundArm, RoundTierTab } from './slices/round.ts';
import type { QdStore } from './types.ts';

const TCLR: Record<RoundTier, string> = {
  s: 'var(--acc)',
  a: 'var(--gt-text)',
  b: 'var(--gt-text-5)',
  c: 'var(--acc2)',
};

/** The ladder by rung, for reading a colour off an effective rank (3054). */
const RANK_TIER: readonly RoundTier[] = ['c', 'b', 'a', 's'];

/**
 * One nominator, as every surface that names them draws them (3054). The same
 * shape on the wheel's hover, the pin card and the browse tile, so "show all
 * the players" is one component's worth of paint in three places.
 */
export interface RoundNomByVM {
  key: string;
  /** `YOU` for me, the player's name otherwise */
  nm: string;
  ini: string;
  bg: string;
  hasAv: boolean;
  avBg: string;
}

/** A nomination decorated with what renders it (prototype `rNoms()`). */
export interface RoundNomVM {
  id: number;
  /** the FIRST nominator: the hue the wedge, the card and the flash wear */
  by: string;
  /** everyone with a quarter on it, earliest first (3054) */
  bys: RoundNomByVM[];
  /** the same names as one line: `YOU & DANA`, `MARCUS, DANA & 2 MORE` */
  byTxt: string;
  at: number;
  nm: string;
  wn: string;
  ini: string;
  fall: string;
  coverCands: readonly ArtCand[];
  /** the seated players' call, BEFORE seconding moves it */
  tier: RoundTier;
  tierClr: string;
  /**
   * The rung it actually draws from — `TIER_RANK[tier] + seconds`, uncapped.
   * Nothing SAYS this: the wedge's width and its rim colour are the whole of
   * how the bump is shown (Marcus, 2026-08-31 — a `S ▸ S+1 · 5 TICKETS` line
   * was tried on the hover and the pin card, and read as clutter).
   */
  eff: number;
  /** the nominator's player hue — wedge flash and pin card outline */
  hue: string;
  w: number;
}

export interface RoundWedgeVM extends Slice {
  nom: RoundNomVM;
  /** scrim over the art: .72 dim, 0 under the pointer / cursor, .12 winner */
  scrim: number;
  isWin: boolean;
}

/** One quarter slot: a gold coin (on) or the dark socket it left (off). */
export interface PipVM {
  key: number;
  on: boolean;
}

function pips(spent: number): PipVM[] {
  return Array.from({ length: R_QUOTA }, (_x, i) => ({ key: i, on: i < R_QUOTA - spent }));
}

const armPair = (arm: RoundArm, key: RoundArm, restBg: string, restClr: string) => {
  const on = arm === key;
  return {
    restV: on ? ('hidden' as const) : ('visible' as const),
    armV: on ? ('visible' as const) : ('hidden' as const),
    bd: on ? 'var(--acc2)' : 'var(--gt-border-2)',
    bg: on ? 'var(--acc2Soft)' : restBg,
    clr: on ? 'var(--acc2)' : restClr,
    /** the draining bar: on while armed, restarts on re-arm */
    fill: on ? 0.22 : 0,
    anim: on,
  };
};

export type ArmPairVM = ReturnType<typeof armPair>;

/**
 * The header chip, the join card and the board's START button — the shell's
 * `rVals()`. Cheap; `headerVM` callers can afford it on every render.
 */
export function roundChipVM(s: QdStore) {
  const r = s.round;
  const me = s.me();
  const inIt = !!r && !!me && r.joined.includes(me.k);
  const host = !!r && !!me && r.by === me.k;
  const owner = !!me && me.role === 'admin';
  const now = s.serverNow();
  const win = r?.spin && spinPose(r.spin, now).landed ? s.gById(r.spin.winner) : null;
  const by = r ? s.user(r.by) : null;
  const byC = chipOf(by);
  // GAMES, not quarters (3054): `noms` is one row per quarter, and a game two
  // people backed is one thing on the wheel. Counting rows would tell the crew
  // there are eight games in the hat when there are five.
  const nomGames = r ? new Set(r.noms.map((n) => n.id)).size : 0;
  return {
    rOn: !!r,
    rIn: inIt,
    rHostOn: host,
    rLifeOn: host || (owner && !!r),
    rStartOn: !r,
    rChipOn: !!r,
    rChipTip:
      r && r.lock
        ? 'TONIGHT: ' + (win ? win.n.toUpperCase() : 'PICKED')
        : inIt
          ? 'BACK TO GAME NIGHT · ' + (r ? r.joined.length : 0) + ' IN'
          : "GAME NIGHT'S ON · " + (r ? r.joined.length : 0) + ' IN — JOIN IT',
    rChipBg: inIt || (r && r.lock) ? 'var(--accSoft)' : 'transparent',
    rChipPulse: !!r && !inIt && !r.lock,
    /** what the chip does: open the page, or join first */
    rChipAct: (inIt || (r && r.lock) ? 'open' : 'join') as 'open' | 'join',
    rCardOn: !!r && !inIt && !r.lock && s.rSeen !== r.id && s.screen !== 'round',
    rCardWho: by ? by.name.toUpperCase() : 'SOMEONE',
    rCardSub:
      nomGames +
      ' NOMINATED · ' +
      (r ? r.joined.length : 0) +
      (r && r.joined.length === 1 ? ' PLAYER IN' : ' PLAYERS IN'),
    cardWhoIni: by ? byC.ini : '?',
    cardWhoBg: by ? byC.hueBg : 'var(--gt-border-2)',
    cardWhoHasAv: by ? byC.hasAv : false,
    cardWhoAv: by ? byC.avBg : '',
  };
}

/**
 * Viewport-dependent geometry for the desktop page (`GT Round.dc.html`
 * `vals()`): two columns at ≥1080, the rail sized for five covers across, the
 * wheel bounded by height as often as width. Pure; the screen measures.
 */
export function roundLayout(W: number, H: number) {
  const twoCol = W >= 1080;
  const railW = twoCol ? Math.max(544, Math.min(624, W - 620)) : 0;
  const stageW = twoCol ? W - railW : W;
  const cardW = stageW < 700 ? 225 : 318;
  const padL = stageW < 720 ? 20 : 40;
  const big = cardW >= 300;
  const px = Math.max(240, Math.min(twoCol ? 620 : 420, H - 240, stageW - cardW - 66 - padL * 2));
  return {
    twoCol,
    railW: twoCol ? railW + 'px' : '100%',
    railBdL: twoCol ? '1px solid var(--gt-line)' : 'none',
    railBdT: twoCol ? 'none' : '1px solid var(--gt-line)',
    gridFlex: twoCol ? '1' : 'none',
    // The cover grid scrolls INSIDE the rail in both modes (Marcus, staging
    // 2026-08-27: the page grew instead). Two columns: it takes whatever
    // height the rail has left. Stacked: capped, so the wheel above is never
    // pushed off by seventy covers.
    gridOv: 'auto' as const,
    gridMaxH: twoCol ? 'none' : '62vh',
    cardW: cardW + 'px',
    qFs: (big ? 78 : 57) + 'px',
    px,
    wheelW: px + 'px',
    wheelH: px + 8 + 'px',
    hubTop: 8 + px / 2 + 'px',
    // The page is the viewport in BOTH modes (Marcus, staging 2026-08-27: it
    // grew with the rail). Two columns: nothing scrolls but the cover grid.
    // Stacked: the body (stage over rail) scrolls inside the page instead.
    pageH: '100vh',
    pageOv: 'hidden' as const,
    bodyOv: twoCol ? ('hidden' as const) : ('auto' as const),
    shellDir: twoCol ? ('row' as const) : ('column' as const),
    stageMinH: twoCol ? '0' : '520px',
    stageOv: twoCol ? ('auto' as const) : ('visible' as const),
    bodyPad: '24px ' + padL + 'px',
    tileMin: '96px',
  };
}

/**
 * The round page. `now` is server time; `hovId` is desktop's cursor slice
 * (`s.rHov`) and is suppressed mid-spin — the wheel is moving under the
 * pointer, so the slice you are over is not a fact worth showing.
 */
export function roundVM(s: QdStore, now: number = s.serverNow()) {
  const r = s.round;
  const me = s.me();
  const meK = me ? me.k : '';
  const chip = roundChipVM(s);
  const arm = s.rArm;
  const users = s.users;

  if (!r) {
    return null;
  }

  /**
   * THE ROSTER THE TIERS ARE READ FROM: whoever is seated tonight, not the
   * crew (Marcus, 2026-08-28). It is derived from `r.joined` and nothing else,
   * so a join or a leave re-tiers every wedge and every rail tab on the next
   * snapshot with no invalidation of our own — the same reason the roster grid
   * below is derived rather than stored. Mirrors `public.round_seat_tier()`,
   * which is the roster the SERVER's draw weighs.
   */
  const seated = r.joined.map((k) => s.user(k)).filter((u): u is NonNullable<ReturnType<typeof s.user>> => !!u);

  const meName = (k: string): string => {
    const u = s.user(k);
    return k === meK ? 'YOU' : u ? u.name.toUpperCase() : 'SOMEONE';
  };

  /** One nominator's avatar + name, for every surface that lists them. */
  const byOf = (k: string): RoundNomByVM => {
    const u = s.user(k);
    const ch = chipOf(u);
    return { key: k, nm: meName(k), ini: ch.ini, bg: ch.hueBg, hasAv: ch.hasAv, avBg: ch.avBg };
  };

  /**
   * The nominators as one line. Two names fit anywhere; past that the line
   * names the two who are visible and counts the rest, so the string cannot
   * grow with the crew and push a 288px hover panel around.
   */
  const byLine = (bys: readonly RoundNomByVM[]): string => {
    const n = bys.length;
    if (!n) return '';
    if (n === 1) return bys[0]?.nm ?? '';
    if (n === 2) return bys[0]?.nm + ' & ' + bys[1]?.nm;
    return bys[0]?.nm + ', ' + bys[1]?.nm + ' & ' + (n - 2) + ' MORE';
  };

  // Nominations decorated with what renders them: the catalog game, its tile
  // art, everyone who put a quarter on it, and the seated players' tier for it
  // — which, plus the seconds, is what buys the tickets.
  const nomOf = (id: number, byKeys: readonly string[], at: number, tierIn?: RoundTier, wIn?: number): RoundNomVM | null => {
    const g = s.gById(id);
    if (!g) return null;
    const e = s.entryOf(id);
    const tier = tierIn ?? rTier(e?.votes, seated);
    const bys = byKeys.map(byOf);
    const by = byKeys[0] ?? '';
    const u = s.user(by);
    const eff = effRank(tier, bys.length);
    const w = wIn ?? ticketsFor(tier, r.wtd, bys.length);
    return {
      id,
      by,
      bys,
      byTxt: byLine(bys),
      at,
      nm: g.n,
      wn: g.n.toUpperCase(),
      ini: initialsOf(g.n),
      fall: fallGradient(g.id),
      coverCands: artCands(g, 'p', s.assets),
      tier,
      // The rim wears the rung it DRAWS from, capped at S's colour — there is
      // no fifth swatch, and a slice that is already the widest on the wheel
      // does not need one to say so.
      tierClr: TCLR[RANK_TIER[Math.min(Math.max(eff, 0), 3)] ?? tier],
      eff,
      hue: u ? chipOf(u).hueBg : 'var(--gt-line)',
      w,
    };
  };

  const inIt = chip.rIn;
  const host = chip.rHostOn;
  const lock = r.lock;
  const spin = r.spin;
  const pose = spin ? spinPose(spin, now) : null;
  const spinning = !!pose && !pose.landed;
  const landed = !!pose && pose.landed;
  const winId = landed && spin ? spin.winner : 0;

  // ONE GAME IS ONE SLICE (3054): `r.noms` is one row per quarter, so the hat
  // is folded by game before it becomes geometry — two people backing the same
  // title is one wide wedge, not two thin ones with the same name on them.
  const hat = groupNoms(r.noms);
  const hatById = new Map(hat.map((h) => [h.id, h]));

  // THE WHEEL IS THE FROZEN TABLE while a spin record stands — the server drew
  // from it, so it is what every screen shows even if the votes moved since.
  // Otherwise it is the live nominations, weighted as the next draw would be.
  const noms: RoundNomVM[] = spin
    ? spin.geom.map((g) => nomOf(g.appid, g.bys, hatById.get(g.appid)?.at ?? 0, g.tier, g.w)).filter((n): n is RoundNomVM => !!n)
    : hat.map((h) => nomOf(h.id, h.bys, h.at)).filter((n): n is RoundNomVM => !!n);
  const slices = rGeom(noms.map((n) => ({ id: n.id, w: n.w })));

  // Only the round page reads these; `rHov` is desktop's cursor slice.
  const hovId = !spinning && s.rHov ? s.rHov.id : null;
  const pinId = pose ? pose.pinId : 0;
  const undimId = spinning ? pinId : hovId;
  const wedges: RoundWedgeVM[] = slices.map((sl, i) => {
    const nom = noms[i] as RoundNomVM;
    const isWin = winId === sl.id;
    return { ...sl, nom, isWin, scrim: undimId === sl.id ? 0 : isWin ? 0.12 : 0.72 };
  });
  const rot = pose ? pose.rot : 0;

  // The pin card names whatever is under the pointer — during the spin that
  // is the live slice, after it the winner.
  const pinN = spinning ? (noms.find((n) => n.id === pinId) ?? null) : winId ? (noms.find((n) => n.id === winId) ?? null) : null;
  const win = winId ? (noms.find((n) => n.id === winId) ?? null) : null;
  const pinBy = pinN ? pinN.by : '';
  const pinByC = chipOf(pinBy ? s.user(pinBy) : null);

  const hovN = hovId ? (noms.find((n) => n.id === hovId) ?? null) : null;

  // The rail is dead once the lever is pulled — UNDO SPIN or RESET reopens it.
  const shut = roundShut(r) || !inIt;
  const mineN = r.noms.filter((n) => n.by === meK).length;
  const left = Math.max(0, R_QUOTA - mineN);
  const othersIn = r.joined.filter((k) => k !== meK).length;
  const nameOf = (k: string): string => {
    const u = s.user(k);
    return u ? u.name.toUpperCase() : 'SOMEONE';
  };

  // Roster and quarter pips derive from the hat, so a non-member can never own
  // a slice: membership is `joined`, counts come from `noms`.
  const roster = seated.map((u) => {
    const ch = chipOf(u);
    const self = u.k === meK;
    const theirs = r.noms.filter((n) => n.by === u.k).length;
    return {
      key: u.k,
      ini: ch.ini,
      bg: ch.hueBg,
      hasAv: ch.hasAv,
      avBg: ch.avBg,
      nm: self ? 'YOU' : u.name.toUpperCase(),
      clr: self ? 'var(--acc)' : 'var(--gt-text-3)',
      host: u.k === r.by,
      pips: pips(theirs),
      /**
       * The leader can take a seat back (Marcus, 2026-08-28). Never their own
       * — that is LEAVE, which also hands the lever on — so the leader's row
       * carries no ✕ and the round can never be kicked empty. Armed like every
       * other destructive control, and armed PER SEAT: `rArm` holds
       * `kick:<key>`, so one `SURE?` is on screen at a time even across rows.
       */
      kickOn: host && !self,
      kick: armPair(arm, ('kick:' + u.k) as RoundArm, 'transparent', 'var(--gt-text-5)'),
      kickTip: roundShut(r)
        ? 'TAKES ' + u.name.toUpperCase() + ' OUT · THE NOMINATIONS STAY'
        : theirs
          ? 'TAKES ' + u.name.toUpperCase() + ' OUT · THEIR ' + theirs + ' COME BACK'
          : 'TAKES ' + u.name.toUpperCase() + ' OUT OF TONIGHT’S GAME NIGHT',
    };
  });

  // The browser is the crew list, tab by tab — the same games the board
  // holds, through the app's one filter state (changelog 2026-08-26).
  const TIERS: ReadonlyArray<[RoundTierTab, string]> = [
    ['all', 'ALL'],
    ['s', 'S'],
    ['a', 'A'],
    ['b', 'B'],
    ['c', 'C'],
  ];
  const tier = s.rTier;
  const shelf = s.list
    .map((e) => {
      const g = s.gById(e.id);
      // Same `seated` roster the wedges are weighed by: the S/A/B/C the rail
      // sorts and counts by has to be the tier that would buy the tickets, or
      // the panel is advertising odds the wheel will not honour.
      return g ? { e, g, tier: rTier(e.votes, seated) } : null;
    })
    .filter((x): x is NonNullable<typeof x> => !!x);
  const rank: Record<RoundTier, number> = { s: 0, a: 1, b: 2, c: 3 };
  // Rounds owns a filter set like every other screen that draws the panel
  // (`store/filterKey.ts`), and names its key rather than reading "the current
  // screen" — this VM knows which board it is building.
  const rFlt = s.fltBy.round;
  const rTags = tagLensOf(s, 'round');
  // ...and the rail's own search box, through the same `passBoard` the three
  // call boards run (2026-08-28). The tab counts below read this SAME set, so a
  // tab promising 2 under a query cannot yield 0.
  const fShelf = shelf.filter((x) => passBoard(x.g, rFlt, rTags, s.bqBy.round, s.pts, s.ownBy));
  const pool = (tier === 'all' ? fShelf : fShelf.filter((x) => x.tier === tier))
    .slice()
    .sort((a, b) => rank[a.tier] - rank[b.tier] || a.g.n.localeCompare(b.g.n));
  const tabs = TIERS.map(([k2, label], i) => {
    const on = tier === k2;
    const n = k2 === 'all' ? fShelf.length : fShelf.filter((x) => x.tier === k2).length;
    return {
      key: k2,
      t: label,
      n,
      ml: i ? '-1px' : '0',
      pos: on ? ('relative' as const) : ('static' as const),
      z: on ? 2 : 'auto',
      bd: on ? 'var(--acc)' : 'var(--gt-border-2)',
      bg: on ? 'var(--accSoft)' : 'transparent',
      clr: on ? 'var(--acc)' : 'var(--gt-text-5)',
      nClr: on ? 'var(--acc)' : 'var(--gt-faint)',
    };
  });
  /**
   * How many avatars a browse tile shows before it starts counting. Three at
   * 22px overlap into ~46px, which still leaves a 96px cover's top-left corner
   * legible; the rest become `+N`.
   */
  const TILE_AVS = 3;
  const tiles = pool.map((x) => {
    const held = hat.find((h) => h.id === x.g.id) ?? null;
    const bys = held ? held.bys : [];
    const mine = bys.includes(meK);
    const first = bys[0] ?? '';
    const u = first ? s.user(first) : null;
    // My own quarter is the coin, so the avatar row is everybody ELSE — the
    // two facts never say the same thing twice in one 22px corner.
    const others = bys.filter((k) => k !== meK);
    const shown = others.slice(0, TILE_AVS).map(byOf);
    // What my quarter would BUY, in the ladder's own words — the same tier the
    // tab strip filed this game under, moved one rung.
    const nextEff = effTierLabel(effRank(x.tier, bys.length + 1));
    return {
      key: x.g.id,
      id: x.g.appid,
      ini: initialsOf(x.g.n),
      fall: fallGradient(x.g.id),
      coverCands: artCands(x.g, 'p', s.assets),
      nm: x.g.n.toUpperCase(),
      coin: mine,
      /** every OTHER nominator, capped — `avMore` counts the ones past the cap */
      avs: shown,
      avMore: Math.max(0, others.length - shown.length),
      heldN: bys.length,
      tip: mine
        ? bys.length > 1
          ? 'YOUR QUARTER + ' + (bys.length - 1) + ' MORE' + (shut ? '' : ' · CLICK TO TAKE YOURS BACK')
          : shut
            ? 'YOUR QUARTER’S ON IT'
            : 'YOUR QUARTER’S ON IT · CLICK TO TAKE IT BACK'
        : held
          ? 'NOM’D BY ' + byLine(bys.map(byOf)) + ' · ' + ago(held.at, now) + (shut || !left ? '' : ' · SECOND IT FOR ' + nextEff)
          : '',
      // The first nominator's hue: it is the one that cannot change while
      // later players pile on, and it is what the wedge already wears. It is
      // also the whole border when nobody has seconded, and the LEFT-hand share
      // of it when somebody has (`hueSplit`).
      bd: held && u ? chipOf(u).hueBg : 'var(--gt-border-2)',
      /** every backer's hue in spend order — the border splits between them */
      bdHues: bys.map((k) => byOf(k).bg),
      /** the hover chip offers what my click would do — nothing if I already hold it */
      chipOn: !mine && !shut,
      lbl: !left ? 'NO QUARTERS' : held ? '+ SECOND IT ▸ 25¢' : 'NOM ▸ 25¢',
      clr: left ? 'var(--gt-text-5)' : 'var(--gt-ghost)',
      cur: shut || (!mine && !left) ? ('default' as const) : ('pointer' as const),
      /** the click does something: back it (mine or not), or take my own back */
      act: !shut && (mine || left > 0),
    };
  });

  const lv = armPair(arm, 'leave', 'transparent', 'var(--gt-text-4)');
  const un = armPair(arm, 'unlock', 'transparent', 'var(--gt-text-5)');
  const ud = armPair(arm, 'undo', 'var(--gt-raised)', 'var(--gt-text-3)');
  const rs = armPair(arm, 'reset', 'var(--gt-raised)', 'var(--gt-text-3)');
  const en = armPair(arm, 'end', 'var(--gt-raised)', 'var(--gt-text-3)');

  return {
    ...chip,
    id: r.id,
    /**
     * The way out names where it goes (Marcus, staging 2026-10-17): a game
     * night started from a SCHEDULE night returns to that night (3071), so
     * "THE BOARD" would be a lie there. Desktop's full label, the phone's short.
     */
    backTxt: r.schedNight ? '◂ BACK TO SCHEDULE' : '◂ BACK TO THE BOARD',
    backTxtM: r.schedNight ? '◂ SCHEDULE' : '◂ BOARD',
    rHeadTxt:
      (r.by === meK ? 'STARTED BY YOU' : 'STARTED BY ' + nameOf(r.by)) +
      ' · ' +
      ago(r.at, now) +
      (host ? '' : ' · ' + nameOf(r.by) + ' PULLS THE LEVER'),
    rUndoOn: host && !!spin,
    rResetOn: host,
    rEndOn: chip.rLifeOn,
    lv,
    un,
    ud,
    rs,
    en,
    // the armed label is a bare SURE? now, so the consequence lives in the tip
    rLvTip: !othersIn
      ? 'YOU’RE THE LAST ONE IN · LEAVING CLOSES GAME NIGHT'
      : host
        ? 'GIVES UP YOUR SEAT · THE LEVER PASSES TO WHOEVER JOINED FIRST'
        : lock
          ? 'GIVES UP YOUR SEAT — THE PICK STANDS'
          : mineN
            ? 'PULLS YOUR ' + mineN + ' BACK AND GIVES UP YOUR SEAT'
            : 'GIVES UP YOUR SEAT IN TONIGHT’S GAME NIGHT',
    rOut: !inIt,
    rCanJoin: !inIt && !lock,
    rShutTxtOn: !inIt && lock,
    rOutTxt: lock ? 'THE PICK STANDS — YOU CAN STILL WATCH IT PLAY OUT' : 'JOIN AND YOU GET THREE QUARTERS TO SPEND',

    // the wheel
    noms,
    wedges,
    rot,
    spinning,
    landed,
    winId,
    winHue: win ? win.hue : 'var(--acc)',
    wheelEmpty: !noms.length,
    emptyTxt: 'NOTHING NOMINATED YET',
    hovId,
    pinId,

    // hover panel (desktop)
    hovOn: !!hovN,
    hovNm: hovN ? hovN.nm : '',
    hovIni: hovN ? hovN.ini : '',
    hovFall: hovN ? hovN.fall : '',
    hovCands: hovN ? hovN.coverCands : [],
    /** EVERY nominator (3054), earliest first — the panel lists them all */
    hovBys: hovN ? hovN.bys : [],
    hovByTxt: hovN ? hovN.byTxt : '',
    hovX: s.rHov ? s.rHov.x : 0,
    hovY: s.rHov ? s.rHov.y : 0,

    // the pin card
    cardOn: !!pinN,
    cardOff: !pinN,
    /** the card names a game, so it opens it — during a spin it is a blur, not a link */
    cardGoId: pinN && !spinning ? pinN.id : 0,
    cardCur: pinN && !spinning ? ('pointer' as const) : ('default' as const),
    cardIni: pinN ? pinN.ini : '',
    cardFall: pinN ? pinN.fall : '',
    cardCands: pinN ? pinN.coverCands : [],
    cardNm: pinN ? pinN.nm : '',
    cardNmClr: win && !spinning ? 'var(--acc)' : 'var(--gt-text)',
    cardBd: pinBy ? pinByC.hueBg : 'var(--gt-border-2)',
    /** every backer's hue in spend order — the card's border splits between them */
    cardHues: pinN ? pinN.bys.map((b) => b.bg) : [],
    cardBw: pinN ? '3px' : '1px',
    cardByOn: !!pinN && !!pinBy && !spinning,
    /** EVERY nominator (3054): the result card credits all of them, not just the first */
    cardBys: pinN ? pinN.bys : [],
    cardByTxt: pinN ? pinN.byTxt : '',

    // leader controls
    wtLbl: r.wtd ? '◆ WEIGHT BY TIER — ON' : '◇ WEIGHT BY TIER — OFF',
    wtBd: r.wtd ? 'var(--acc)' : 'var(--gt-border-2)',
    wtBg: r.wtd ? 'var(--accSoft)' : 'transparent',
    wtClr: r.wtd ? 'var(--acc)' : 'var(--gt-text-5)',
    wtOp: roundShut(r) ? 0.42 : 1,
    wtPE: roundShut(r) ? ('none' as const) : ('auto' as const),
    wtCur: roundShut(r) ? ('default' as const) : ('pointer' as const),
    wtTip: !roundShut(r) ? '' : lock ? 'LOCKED IN · THE ODDS ARE SETTLED' : 'SPUN · UNDO SPIN TO CHANGE THE ODDS',
    canSpin: !spin && noms.length > 0,
    needNoms: !spin && !noms.length,
    spinLbl: spinning ? 'SPINNING…' : 'SPIN ▸',
    /** the wheel is moving: SPIN is shown (busy) but nothing else changes */
    stSpinning: spinning,
    stWin: landed && !lock,
    stLock: landed && lock,
    winNm: win ? win.wn : '',

    rosterTxt: r.joined.length + ' OF ' + users.length,
    roster,

    tabs,
    tiles,
    pips: pips(mineN),
    rFltMatch: fShelf.length + ' OF ' + shelf.length + ' PARTY GAMES MATCH',
    /* The rail's box. `rFltMatch` above already counts the set it narrows —
       the Filter Bar carries that line here. */
    bq: s.bqBy.round,
    clrOn: mineN > 0 && !shut,
    clrLbl: '✕ TAKE BACK MY ' + mineN,
    shutOn: shut,
    nomOp: shut ? 0.38 : 1,
    nomPE: shut ? ('none' as const) : ('auto' as const),
    shutTxt: !inIt ? 'JOIN TO NOMINATE' : lock ? 'LOCKED IN' : 'SPUN · UNDO SPIN REOPENS IT',
    railEmpty: !pool.length,
    railEmptyTxt: !shelf.length
      ? 'THE PARTY LIST IS EMPTY — ADD GAMES FROM DISCOVER FIRST'
      : !fShelf.length
        ? 'NO PARTY GAMES MATCH YOUR FILTERS'
        : 'NO MATCHES IN THIS TIER — CHECK YOUR FILTERS',

    // phone-only: the sheet, and the trigger that states its own reason
    sheetOn: s.rSheet,
    // GAMES, not quarters: the sheet's head counts what is on the wheel (3054).
    hatTxt: hat.length + ' NOMINATED',
    addLbl: !inIt ? 'JOIN TO NOMINATE' : lock ? 'LOCKED IN' : spin ? 'SPUN · UNDO SPIN REOPENS' : '+ NOMINATE',
    addBd: shut ? 'var(--gt-border-2)' : 'var(--acc)',
    addBg: shut ? 'transparent' : 'var(--accSoft)',
    addClr: shut ? 'var(--gt-ghost)' : 'var(--acc)',
    addCur: shut ? ('default' as const) : ('pointer' as const),
  };
}

export type RoundVM = NonNullable<ReturnType<typeof roundVM>>;
